import React, { useMemo } from 'react';
import { Box, Skeleton, Alert, Typography } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  CommonSeriesSettings,
  Export,
} from 'devextreme-react/chart';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard } from '../components/DashboardCard';

const END_REASON_ID_COLORS: Record<number, string> = {
  1: '#EF5350',  // Drop Out
  2: '#FF9800',  // Withdrawal
  3: '#26A69A',  // N/A
  4: '#FFA726',  // Other
  5: '#66BB6A',  // Graduated
  6: '#42A5F5',  // Persisted Next Grade
  7: '#5C6BC0',  // Persisted Same Grade
  8: '#2E7D32',  // Graduated w/ Rigorous
  9: '#81C784',  // Graduated w/o Rigorous
  10: '#757575', // Deceased
  11: '#BDBDBD', // Unknown
};

const DEFAULT_COLOR = '#9E9E9E';

interface StackedChartDataPoint {
  year: string;
  [key: string]: string | number;
}

const EndReasonTypeChartContent = () => {
  const { data, loading, error } = useEndReasonTypeData();

  const { chartData, endReasonTypes, typeToIdMap } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], endReasonTypes: [], typeToIdMap: {} };
    }

    const uniqueYears = [...new Set(data.map((d) => d.year))].sort();
    const uniqueTypes = [...new Set(data.map((d) => d.endReasonTypeName))].sort();
    
    const typeToIdMap: Record<string, number> = {};
    data.forEach(d => {
        if (!typeToIdMap[d.endReasonTypeName]) {
            typeToIdMap[d.endReasonTypeName] = d.endReasonTypeId;
        }
    });

    const mappedData: StackedChartDataPoint[] = uniqueYears.map((year) => {
      const yearData: StackedChartDataPoint = { year: `${year}-${year + 1}` };
      uniqueTypes.forEach((type) => {
        const item = data.find((d) => d.year === year && d.endReasonTypeName === type);
        yearData[type] = item?.percentageSubmitted || 0;
      });
      return yearData;
    });

    return { chartData: mappedData, endReasonTypes: uniqueTypes, typeToIdMap };
  }, [data]);

  if (loading) return <Skeleton variant="rounded" height={400} />;
  
  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary" sx={{ py: 5 }}>
        No end reason type data available for selected years
      </Typography>
    );
  }

  return (
    <Chart dataSource={chartData} height={400}>
      <CommonSeriesSettings type="stackedbar" barPadding={0.3} />
      <ArgumentAxis />
      <ValueAxis
        visualRange={{ startValue: 0, endValue: 100 }}
        tickInterval={10}
        title="Percentage (%)"
      />
      {endReasonTypes.map((type) => {
        // Get ID from the map, then get Color from the ID
        const typeId = typeToIdMap[type];
        const seriesColor = END_REASON_ID_COLORS[typeId] ?? DEFAULT_COLOR;

        return (
            <Series
                key={type}
                valueField={type}
                argumentField="year"
                name={type}
                color={seriesColor}
                barWidth={100}
            />
        );
      })}
      <Legend visible={true} horizontalAlignment="center" verticalAlignment="bottom" />
      <Tooltip
        enabled={true}
        customizeTooltip={(arg: any) => {
          const year = parseInt(arg.argument.split('-')[0]);
          const item = data.find((d) => d.year === year && d.endReasonTypeName === arg.seriesName);
          if (item) {
            return {
              text: `${arg.seriesName}: ${arg.value?.toFixed(1)}% (${item.studentCount}/${item.totalEnrolled} students)`,
            };
          }
          return { text: `${arg.seriesName}: ${arg.value?.toFixed(1)}%` };
        }}
      />
      <Export enabled={true} />
    </Chart>
  );
};

export const EndReasonTypeChartCard = () => {
  return (
    <DashboardCard title="End Reason Type Distribution">
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <EndReasonTypeFilterControls />
        <Box sx={{ flexGrow: 1, minHeight: 0, mt: 2 }}>
          <EndReasonTypeChartContent />
        </Box>
      </Box>
    </DashboardCard>
  );
};